import { FC, MouseEvent, useEffect, useState } from 'react';

import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import { useClass, useEditClass } from '../../../services/class/class.service';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import ClassForm from '../ClassForm/ClassForm';
import { ClassFormErrors, ClassFormValues } from '../ClassForm/ClassForm.types';

const EditClass: FC = () => {
  const navigateWithToast = useNavigateWithToast();
  const queryClient = useQueryClient();
  const { classNo } = useParams();
  const _classNo = classNo as string;
  const { data, isError, error } = useClass({ classNo: _classNo }, true);
  const { mutate, isError: editClassIsError, isLoading, error: editClassError } = useEditClass(_classNo);

  useGenericErrorMessage([
    { isError, error },
    { error: editClassError, isError: editClassIsError },
  ]);

  const classData = data?.data;
  useEffect(() => {
    if (classData) {
      const classValues = {
        classNo: classData.classNo,
        validOnWeb: classData.validOnWeb,
        statusNo: classData.statusNo,
        pcaClassNo: classData.pcaClassNo,
        priceOptionNo: classData.priceOptionNo,
        printInKitchen: classData.printInKitchen,
        allowDiscount: classData.allowDiscount,
        taxExempted: classData.taxExempted,
        allowHalf: classData.allowHalf,
        displayDesc: classData.displayDesc,
        mnemonic: classData.mnemonic,
        seqNo: classData.seqNo,
        description: classData.description,
        longDescription: classData.longDescription,
      };
      setFormValues(classValues);
      setInitialFormValues(classValues);
    }
  }, [classData]);
  const [formValues, setFormValues] = useState<ClassFormValues>({
    classNo: undefined,
    statusNo: 0,
    pcaClassNo: undefined,
    priceOptionNo: undefined,
    seqNo: undefined,
    printInKitchen: undefined,
    allowDiscount: undefined,
    taxExempted: undefined,
    allowHalf: undefined,
    validOnWeb: false,
    mnemonic: '',
    description: '',
    longDescription: '',
    displayDesc: '',
  });

  const [initialFormValues, setInitialFormValues] = useState<ClassFormValues>({
    classNo: undefined,
    statusNo: 0,
    pcaClassNo: undefined,
    priceOptionNo: undefined,
    seqNo: undefined,
    printInKitchen: undefined,
    allowDiscount: undefined,
    taxExempted: undefined,
    allowHalf: undefined,
    validOnWeb: false,
    mnemonic: '',
    description: '',
    longDescription: '',
    displayDesc: '',
  });

  const [formErrors, setFormErrors] = useState<ClassFormErrors>({
    classNo: '',
    displayDesc: '',
    mnemonic: '',
    longDescription: '',
    description: '',
    printInKitchen: '',
    allowDiscount: '',
    taxExempted: '',
    allowHalf: '',
    validOnWeb: '',
    statusNo: '',
    pcaClassNo: '',
    priceOptionNo: '',
    seqNo: '',
  });

  const validateFormValues = () => {
    let error = false;
    let errorMessages: ClassFormErrors = {
      classNo: '',
      displayDesc: '',
      mnemonic: '',
      longDescription: '',
      description: '',
      printInKitchen: '',
      allowDiscount: '',
      taxExempted: '',
      allowHalf: '',
      validOnWeb: '',
      statusNo: '',
      pcaClassNo: '',
      priceOptionNo: '',
      seqNo: '',
    };
    if ([null, undefined, ''].includes(formValues.description)) {
      errorMessages.description = 'Short Description is required.';
      error = true;
    }
    if (
      [null, undefined, ''].includes(formValues.description) ||
      formValues.description.length < 1 ||
      formValues.description.length > 50
    ) {
      errorMessages.description = 'Short Description must be 1-50 characters.';
      error = true;
    }
    if (formValues?.description != null && formValues.description !== '') {
      let i = 0;
      let upperCaseCharCnt = 0;
      let character;
      let description = formValues.description;
      /* Check to see if there are 2 uppercase characters in the description*/
      while (i <= description.length) {
        character = description.charAt(i);
        if (!isNaN(Number(character) * 1)) {
          //console.log(character + 'character is numeric');
        } else {
          if (character === character.toUpperCase()) {
            upperCaseCharCnt++;
          }
        }
        i++;
      }
      if (upperCaseCharCnt === 0) {
        error = true;
        errorMessages.description = 'There are no capital letters in the description';
      }

      if (upperCaseCharCnt > 2) {
        error = true;
        errorMessages.description = 'There must be 1 or 2 capital letters in the description';
      }
    }
    if (
      [null, undefined, ''].includes(formValues.longDescription) ||
      formValues.longDescription.length < 1 ||
      formValues.longDescription.length > 30
    ) {
      errorMessages.longDescription = 'Long Description must be 1-30 characters.';
      error = true;
    }
    if ([null, undefined, -1, ''].includes(formValues.pcaClassNo)) {
      errorMessages.pcaClassNo = 'PCA Class Number is required.';
      error = true;
    }
    if ([null, undefined, -1, ''].includes(formValues.seqNo)) {
      errorMessages.seqNo = 'Sequence Number is required.';
      error = true;
    }
    if (
      [null, undefined, ''].includes(formValues.displayDesc) ||
      formValues.displayDesc.length < 1 ||
      formValues.displayDesc.length > 9
    ) {
      errorMessages.displayDesc = 'BOH Description must be 1-9 characters.';
      error = true;
    }

    setFormErrors((formErrors) => {
      return { ...formErrors, ...errorMessages };
    });
    return error;
  };
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const checkForUnsavedChanges = () => {
    if (
      initialFormValues.classNo === formValues.classNo &&
      initialFormValues.priceOptionNo === formValues.priceOptionNo &&
      initialFormValues.description === formValues.description &&
      initialFormValues.longDescription === formValues.longDescription &&
      initialFormValues.pcaClassNo === formValues.pcaClassNo &&
      initialFormValues.seqNo === formValues.seqNo &&
      initialFormValues.statusNo === formValues.statusNo &&
      initialFormValues.validOnWeb === formValues.validOnWeb &&
      initialFormValues.printInKitchen === formValues.printInKitchen &&
      initialFormValues.allowDiscount === formValues.allowDiscount &&
      initialFormValues.taxExempted === formValues.taxExempted &&
      initialFormValues.allowHalf === formValues.allowHalf &&
      initialFormValues.displayDesc === formValues.displayDesc
    ) {
      setUnsavedChanges(false);
      return;
    } else {
      setUnsavedChanges(true);
      return;
    }
  };
  useEffect(() => {
    checkForUnsavedChanges();
  }, [formValues]);

  const handleSave = (event: MouseEvent) => {
    event.preventDefault();
    const isError = validateFormValues();
    if (isError) return;
    setUnsavedChanges(false);
    mutate(
      {
        classNo: formValues.classNo as number,
        displayDesc: formValues.displayDesc,
        validOnWeb: formValues.validOnWeb as boolean,
        statusNo: formValues.statusNo as number,
        pcaClassNo: formValues.pcaClassNo as number,
        priceOptionNo: formValues.priceOptionNo as number,
        printInKitchen: formValues.printInKitchen as boolean,
        allowDiscount: formValues.allowDiscount as boolean,
        taxExempted: formValues.taxExempted as boolean,
        allowHalf: formValues.allowHalf as boolean,
        mnemonic: formValues.mnemonic as string,
        seqNo: formValues.seqNo as number,
        description: formValues.description,
        longDescription: formValues.longDescription,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['class', _classNo]);
          navigateWithToast(`/tables/classes`, {
            label: 'Class has been updated successfully',
            id: 'classUpdateSuccess',
          });
        },
      }
    );
  };

  return (
    <>
      <ClassForm
        formValues={formValues}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        setFormValues={setFormValues}
        headerTitle={`View Class ${classData?.classNo ? `- ${classData?.classNo}` : ''}`}
        saveDisabled={!unsavedChanges}
        handleSave={handleSave}
        isButtonLoading={isLoading}
        disabledInputs={['moaClassNo', 'classNo', 'mnemonic']}
        hasImageForm={true}
      />
      <UnsavedChangesModal unsavedChangesExist={unsavedChanges} />
    </>
  );
};

export default EditClass;
